import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import NotificationTab from './components/NotificationTab';
import { useNotifications } from '../../hooks/useNotifications';

// ... existing code ...

export default function ProfilePage() {
  const { user, username, avatarUrl, updateUsername, updateAvatar, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [badges, setBadges] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications'>('profile');
  const [likeStats, setLikeStats] = useState({ diary: 0, material: 0 });
  const { unreadCount } = useNotifications();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchProfile();
    fetchBadges();
    fetchLikeStats();
  }, [user, navigate, authLoading]);

  useEffect(() => {
    if (username) setNewUsername(username);
  }, [username]);

  const fetchProfile = async () => {
    if (!user) return;
    try {
      const { data } = await supabase.from('user_profiles').select('*').eq('user_id', user.id).maybeSingle();
      if (data) { setProfile(data); setNewUsername(data.username || ''); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchLikeStats = async () => {
    if (!user) return;
    try {
      // 自分の日記へのいいね数
      const { data: myDiaries } = await supabase.from('diary_entries').select('id').eq('user_id', user.id);
      const diaryIds = (myDiaries || []).map((d: any) => d.id);
      let diaryLikes = 0;
      if (diaryIds.length > 0) {
        const { count } = await supabase.from('diary_likes').select('*', { count: 'exact', head: true }).in('diary_entry_id', diaryIds);
        diaryLikes = count || 0;
      }
      // 自分の資料へのいいね数
      const { data: myMaterials } = await supabase.from('study_materials').select('id').eq('user_id', user.id);
      const materialIds = (myMaterials || []).map((m: any) => m.id);
      let materialLikes = 0;
      if (materialIds.length > 0) {
        const { count } = await supabase.from('material_likes').select('*', { count: 'exact', head: true }).in('material_id', materialIds);
        materialLikes = count || 0;
      }
      setLikeStats({ diary: diaryLikes, material: materialLikes });
    } catch (e) { console.error(e); }
  };

  const fetchBadges = async () => {
    if (!user) return;
    try {
      const { data: sessions } = await supabase.from('study_sessions').select('duration_minutes').eq('user_id', user.id);
      const totalStudyHours = Math.floor((sessions?.reduce((s: number, x: any) => s + (x.duration_minutes || 0), 0) || 0) / 60);
      const { count: diaryCount } = await supabase.from('diary_entries').select('*', { count: 'exact', head: true }).eq('user_id', user.id);
      const { data: quizData } = await supabase.from('quiz_mastery').select('correct_count').eq('user_id', user.id);
      const totalCorrect = quizData?.reduce((s: number, q: any) => s + (q.correct_count || 0), 0) || 0;

      // グループ目標4週連続達成バッジの判定
      // 過去4週分の週開始日を計算
      const now = new Date();
      const getWeekStart = (d: Date) => {
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        const ws = new Date(d);
        ws.setDate(diff);
        ws.setHours(0, 0, 0, 0);
        return ws;
      };
      const currentWeekStart = getWeekStart(now);
      const fourWeeksAgo = new Date(currentWeekStart);
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

      // 過去4週間に自分がactorとしてgroup_goal_achieved通知を送った週の数を確認
      const { data: goalAchievedNotifs } = await supabase
        .from('notifications')
        .select('created_at, target_id')
        .eq('actor_user_id', user.id)
        .eq('type', 'group_goal_achieved')
        .gte('created_at', fourWeeksAgo.toISOString());

      // 週ごとにユニークな達成をカウント（同じグループ・同じ週は1回）
      const achievedWeeks = new Set<string>();
      (goalAchievedNotifs || []).forEach((n: any) => {
        const ws = getWeekStart(new Date(n.created_at));
        achievedWeeks.add(`${n.target_id}_${ws.toISOString()}`);
      });
      // 4週分（各週1回以上達成）かどうか
      const weekKeys = new Set<string>();
      (goalAchievedNotifs || []).forEach((n: any) => {
        const ws = getWeekStart(new Date(n.created_at));
        weekKeys.add(ws.toISOString());
      });
      const consecutiveGoalAchieved = weekKeys.size >= 4;

      setBadges([
        { id: 'study_10h', icon: 'ri-time-line', title: '学習10時間達成', description: '累計10時間の学習を達成', color: 'from-blue-500 to-blue-600', earned: totalStudyHours >= 10 },
        { id: 'study_50h', icon: 'ri-medal-line', title: '学習50時間達成', description: '累計50時間の学習を達成', color: 'from-purple-500 to-purple-600', earned: totalStudyHours >= 50 },
        { id: 'study_100h', icon: 'ri-trophy-line', title: '学習100時間達成', description: '累計100時間の学習を達成', color: 'from-yellow-500 to-yellow-600', earned: totalStudyHours >= 100 },
        { id: 'diary_10', icon: 'ri-book-2-line', title: '日記10件投稿', description: '10件の日記を投稿', color: 'from-green-500 to-green-600', earned: (diaryCount || 0) >= 10 },
        { id: 'diary_50', icon: 'ri-book-open-line', title: '日記50件投稿', description: '50件の日記を投稿', color: 'from-teal-500 to-teal-600', earned: (diaryCount || 0) >= 50 },
        { id: 'quiz_100', icon: 'ri-lightbulb-line', title: 'クイズ100問正解', description: '100問のクイズに正解', color: 'from-orange-500 to-orange-600', earned: totalCorrect >= 100 },
        { id: 'quiz_500', icon: 'ri-star-line', title: 'クイズ500問正解', description: '500問のクイズに正解', color: 'from-red-500 to-red-600', earned: totalCorrect >= 500 },
        { id: 'quiz_1000', icon: 'ri-vip-crown-line', title: 'クイズ1000問正解', description: '1000問のクイズに正解', color: 'from-pink-500 to-pink-600', earned: totalCorrect >= 1000 },
        { id: 'group_goal_4weeks', icon: 'ri-group-line', title: 'グループ目標4週連続達成', description: 'グループの週間目標を4週連続で達成', color: 'from-emerald-500 to-teal-600', earned: consecutiveGoalAchieved },
      ]);
    } catch (e) { console.error(e); }
  };

  const validateUsername = (value: string): boolean => {
    setUsernameError('');
    if (value.length < 3 || value.length > 20) { setUsernameError('ユーザー名は3〜20文字で入力してください'); return false; }
    if (!/^[a-zA-Z0-9_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]+$/.test(value)) { setUsernameError('英数字、日本語、アンダースコアのみ使用できます'); return false; }
    return true;
  };

  const handleSaveUsername = async () => {
    if (!validateUsername(newUsername)) return;
    setIsSaving(true);
    try {
      const { error } = await updateUsername(newUsername);
      if (!error) { setIsEditingUsername(false); setShowSuccessToast(true); setTimeout(() => setShowSuccessToast(false), 3000); await fetchProfile(); }
      else setUsernameError(error);
    } catch { setUsernameError('エラーが発生しました'); } finally { setIsSaving(false); }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const { error } = await updateAvatar(file);
      if (!error) { setShowSuccessToast(true); setTimeout(() => setShowSuccessToast(false), 3000); await fetchProfile(); }
      else { console.error(error); }
    } catch (err) { console.error(err); } finally { setUploadingAvatar(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"><div className="text-gray-600 dark:text-gray-400">読み込み中...</div></div>;
  }

  const earnedBadges = badges.filter(b => b.earned);
  const unearnedBadges = badges.filter(b => !b.earned);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Header />

      {/* Hero Header */}
      <div className="hero-dark-force hero-gradient-animated relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/20 via-teal-600/20 to-green-600/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-6 mb-6">
            <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-white/20 border-4 border-white/30 flex items-center justify-center">
                {avatarUrl || profile?.avatar_url ? (
                  <img src={avatarUrl || profile?.avatar_url || ''} alt="アバター" className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-user-line text-white text-4xl"></i>
                )}
              </div>
              <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {uploadingAvatar ? <i className="ri-loader-4-line text-white text-2xl animate-spin"></i> : <i className="ri-camera-line text-white text-2xl"></i>}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1">プロフィール</h1>
              <p className="text-white/70 text-sm">アバターをクリックして画像を変更できます</p>
            </div>
          </div>

          {/* いいね数コンパクト表示 */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/20">
              <i className="ri-heart-fill text-red-400 text-sm"></i>
              <span className="text-white text-sm font-semibold">日記いいね: <span className="text-red-300">{likeStats.diary}</span></span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/20">
              <i className="ri-heart-fill text-red-400 text-sm"></i>
              <span className="text-white text-sm font-semibold">資料いいね: <span className="text-red-300">{likeStats.material}</span></span>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => setActiveTab('notifications')}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-500/30 backdrop-blur-sm rounded-full border border-red-400/40 cursor-pointer hover:bg-red-500/40 transition-colors animate-pulse"
              >
                <i className="ri-notification-3-fill text-red-300 text-sm"></i>
                <span className="text-white text-sm font-semibold">未読通知: <span className="text-red-300">{unreadCount}件</span></span>
              </button>
            )}
          </div>

          {/* ユーザー名編集 */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
            <label className="block text-sm font-medium text-white/80 mb-2">ユーザー名</label>
            {!isEditingUsername ? (
              <div className="flex items-center gap-3">
                <span className="text-2xl font-semibold text-white">{username || profile?.username || 'ユーザー名未設定'}</span>
                <button onClick={() => setIsEditingUsername(true)} className="w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-colors cursor-pointer">
                  <i className="ri-pencil-line text-white text-lg"></i>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <input type="text" value={newUsername} onChange={e => { setNewUsername(e.target.value); if (e.target.value) validateUsername(e.target.value); else setUsernameError(''); }}
                  className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm" placeholder="ユーザー名を入力" disabled={isSaving} />
                {usernameError && <p className="text-sm text-red-300 flex items-center gap-2"><i className="ri-error-warning-line"></i>{usernameError}</p>}
                <div className="flex gap-2">
                  <button onClick={handleSaveUsername} disabled={isSaving || !!usernameError || !newUsername} className="px-4 py-2 bg-white text-emerald-600 rounded-lg font-medium hover:bg-white/90 disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer">{isSaving ? '保存中...' : '保存'}</button>
                  <button onClick={() => { setIsEditingUsername(false); setNewUsername(profile?.username || username || ''); setUsernameError(''); }} disabled={isSaving} className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer">キャンセル</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* タブ切り替え */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-14 sm:top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-0">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'profile' ? 'border-teal-500 text-teal-600 dark:text-teal-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            >
              <i className="ri-user-line mr-1.5"></i>アカウント・バッジ
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`relative px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'notifications' ? 'border-teal-500 text-teal-600 dark:text-teal-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            >
              <i className="ri-notification-3-line mr-1.5"></i>通知
              {unreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{unreadCount}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* コンテンツ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 flex-1 w-full">
        {activeTab === 'notifications' ? (
          <NotificationTab />
        ) : (
          <div className="space-y-8">
            {/* アカウント情報 */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6">アカウント情報</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">メールアドレス</label>
                  <div className="text-lg text-gray-900 dark:text-white">{user?.email || '不明'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">登録日</label>
                  <div className="text-lg text-gray-900 dark:text-white">
                    {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('ja-JP') : user?.created_at ? new Date(user.created_at).toLocaleDateString('ja-JP') : '不明'}
                  </div>
                </div>
              </div>
            </div>

            {/* バッジ */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6">バッジコレクション</h2>
              {earnedBadges.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <i className="ri-trophy-line text-yellow-500"></i>獲得済みバッジ ({earnedBadges.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {earnedBadges.map(badge => (
                      <div key={badge.id} className={`bg-gradient-to-br ${badge.color} rounded-xl p-6 text-white shadow-lg transform hover:scale-105 transition-transform`}>
                        <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mb-4"><i className={`${badge.icon} text-2xl`}></i></div>
                        <h4 className="font-bold text-lg mb-2">{badge.title}</h4>
                        <p className="text-sm text-white/90">{badge.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {unearnedBadges.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <i className="ri-lock-line text-gray-400"></i>未獲得バッジ ({unearnedBadges.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {unearnedBadges.map(badge => (
                      <div key={badge.id} className="bg-gray-100 dark:bg-gray-700 rounded-xl p-6 opacity-60">
                        <div className="w-12 h-12 flex items-center justify-center bg-gray-200 dark:bg-gray-600 rounded-full mb-4"><i className={`${badge.icon} text-2xl text-gray-400`}></i></div>
                        <h4 className="font-bold text-lg mb-2 text-gray-700 dark:text-gray-300">{badge.title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{badge.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />

      {showSuccessToast && (
        <div className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
          <i className="ri-check-line text-xl"></i>
          <span className="font-medium">更新しました</span>
        </div>
      )}
    </div>
  );
}
